import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import type {
  MutationStartConversationArgs,
  StartConversationPayload,
  Conversation,
} from "@casey/db-types";
import { START_INTERVIEW_SESSION } from "../gql";

interface StartConversationResponse {
  startConversation: StartConversationPayload;
}

export function useCreateConversation() {
  const [startConversationMutation, { data, loading, error }] = useMutation<
    StartConversationResponse,
    MutationStartConversationArgs
  >(START_INTERVIEW_SESSION, {
    errorPolicy: "all",
    onError: (error) => {
      console.error("Interview session start error:", error);
    },
  });

  const createSession = useCallback(
    async (connectionCode: string, sessionProperties?: Record<string, any>) => {
      // Validate invite code
      if (!connectionCode || connectionCode.trim() === "") {
        throw new Error("Invite code is required and cannot be empty");
      }

      console.log("Starting session with invite code:", connectionCode);

      // Capture session properties (UTM params, contact ID, user agent, etc.)
      const properties = sessionProperties || {};

      // Add user agent and page info if not provided and running in browser
      if (typeof window !== "undefined") {
        if (!properties.user_agent) {
          properties.user_agent = navigator.userAgent;
        }
        if (!properties.landing_page) {
          properties.landing_page = window.location.href;
        }
        if (!properties.referrer && document.referrer) {
          properties.referrer = document.referrer;
        }

        // Capture UTM params from URL
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has("utm_campaign")) {
          properties.utm_campaign = urlParams.get("utm_campaign");
        }
        if (urlParams.has("utm_source")) {
          properties.utm_source = urlParams.get("utm_source");
        }
        if (urlParams.has("utm_medium")) {
          properties.utm_medium = urlParams.get("utm_medium");
        }
        if (urlParams.has("utm_content")) {
          properties.utm_content = urlParams.get("utm_content");
        }
        if (urlParams.has("utm_term")) {
          properties.utm_term = urlParams.get("utm_term");
        }

        // Capture contact ID (cid) from URL
        if (urlParams.has("cid")) {
          properties.cid = urlParams.get("cid");
        }
      }

      try {
        const result = await startConversationMutation({
          variables: {
            input: {
              pConnectionCode: connectionCode.trim(),
              // Serialize to JSON string for PostgreSQL JSONB
              pSessionProperties: JSON.stringify(properties),
            },
          },
        });

        const createdSession = result.data?.startConversation?.conversation;

        if (createdSession) {
          // Return the session - organization info is in the context
          return createdSession;
        }

        throw new Error("No session returned from mutation");
      } catch (err) {
        console.error("Error creating interview session:", err);

        // Error is logged and will be available in the error state
        throw err;
      }
    },
    [startConversationMutation],
  );

  return {
    createSession,
    session: data?.startConversation?.conversation,
    loading,
    error: error?.message || null,
  };
}
